import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { api } from '../services/api';
import CopyReview from './CopyReview';
import { otherPools, retiredOtherPools } from '../utils/copyPoolsView';
import './CopyPoolsPage.css';

/**
 * Copy Pools: the generic Mister Lexicon copy the public site picks from at
 * random (D1 `copy_pools`). Every change writes straight to D1 and the public
 * worker reads it on the next page load; there is no publish step.
 *
 * Two views (2026-10-31): Game review, every game pool grouped by page with
 * Approve / Toss / Edit (only approved lines serve in a game), and Other
 * pools, the category editor for everything else (categories on the left,
 * lines on the right).
 */

interface PoolCategory {
  category: string;
  group: string;
  label: string;
  slot: string;
  wired: boolean;
  review: string | null;
  retired?: boolean;
  registered: boolean;
  total: number;
  active: number;
}

interface PoolLine {
  id: number;
  category: string;
  line: string;
  active: number;
  sort_order: number;
  tag: string | null;
  created_at: string;
  updated_at: string;
}

interface LineWrite {
  row: PoolLine;
  warnings: string[];
}

export default function CopyPoolsPage() {
  const [view, setView] = useState<'review' | 'other'>('review');
  const [allCategories, setAllCategories] = useState<PoolCategory[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [lines, setLines] = useState<PoolLine[]>([]);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [newLine, setNewLine] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      const data = await api.get('/api/copy-pools') as { categories: PoolCategory[] };
      setAllCategories(data.categories);
      setSelected((cur) => cur ?? data.categories.find((c) => !c.review && !c.retired)?.category ?? null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const loadLines = useCallback(async (category: string) => {
    setLoadingLines(true);
    try {
      const data = await api.get(`/api/copy-pools/${category}`) as { lines: PoolLine[] };
      setLines(data.lines);
      setDrafts({});
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingLines(false);
    }
  }, []);

  // Game pools are reviewed on the Game review view, never switched on or off here.
  // Retired pools stay hidden unless Show retired pools is on (rows kept).
  const categories = useMemo(() => otherPools(allCategories, showRetired), [allCategories, showRetired]);
  const retiredCount = useMemo(() => retiredOtherPools(allCategories).length, [allCategories]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    if (selected) loadLines(selected);
    setNewLine('');
    setNotice(null);
  }, [selected, loadLines]);

  const groups = useMemo(() => {
    const out: { group: string; items: PoolCategory[] }[] = [];
    for (const c of categories) {
      const g = out.find((x) => x.group === c.group);
      if (g) g.items.push(c);
      else out.push({ group: c.group, items: [c] });
    }
    return out;
  }, [categories]);

  const current = categories.find((c) => c.category === selected) || null;
  const activeCount = lines.filter((l) => l.active === 1).length;

  // Every write refreshes the category counts so the left column stays true.
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await loadCategories();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const showWarnings = (warnings: string[]) => {
    if (warnings.length) setNotice(`Saved, with linter warnings: ${warnings.join(' ')}`);
  };

  const addLine = () => {
    if (!selected || !newLine.trim()) return;
    run(async () => {
      const res = await api.post(`/api/copy-pools/${selected}`, { line: newLine }) as LineWrite;
      setLines((ls) => [...ls, res.row]);
      setNewLine('');
      showWarnings(res.warnings);
    });
  };

  const saveLine = (id: number) => {
    const text = drafts[id];
    if (text === undefined) return;
    run(async () => {
      const res = await api.put(`/api/copy-pools/lines/${id}`, { line: text }) as LineWrite;
      setLines((ls) => ls.map((l) => (l.id === id ? res.row : l)));
      setDrafts((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
      showWarnings(res.warnings);
    });
  };

  const toggleActive = (line: PoolLine) => {
    run(async () => {
      const res = await api.put(`/api/copy-pools/lines/${line.id}`, { active: line.active === 1 ? 0 : 1 }) as LineWrite;
      setLines((ls) => ls.map((l) => (l.id === line.id ? res.row : l)));
    });
  };

  const removeLine = (line: PoolLine) => {
    if (!window.confirm(`Delete this line?\n\n${line.line}`)) return;
    run(async () => {
      await api.delete(`/api/copy-pools/lines/${line.id}`);
      setLines((ls) => ls.filter((l) => l.id !== line.id));
    });
  };

  const move = (index: number, delta: number) => {
    if (!selected) return;
    const target = index + delta;
    if (target < 0 || target >= lines.length) return;
    const ids = lines.map((l) => l.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(async () => {
      const res = await api.put(`/api/copy-pools/${selected}/order`, { ids }) as { lines: PoolLine[] };
      setLines(res.lines);
    });
  };

  const submitOnCmdEnter = (e: KeyboardEvent<HTMLTextAreaElement>, submit: () => void) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="copypools-page">
      <div className="copypools-header">
        <h1 className="copypools-title">Copy Pools</h1>
        <p className="copypools-subtitle">
          {view === 'review'
            ? 'Lines serve only once you approve them. Unreviewed and tossed lines never appear on the site, and a pool with no approved line leaves its slot empty. Changes go live on the next page load.'
            : 'Mister Lexicon lines the site does not show yet (carousel, newsletter, daily set). Changes go live on the next page load. Only active lines are picked. A live slot with no active lines renders nothing.'}
        </p>
        <div className="copypools-views" role="tablist">
          <button role="tab" aria-selected={view === 'review'} className={view === 'review' ? 'active' : ''} onClick={() => setView('review')}>Game review</button>
          <button role="tab" aria-selected={view === 'other'} className={view === 'other' ? 'active' : ''} onClick={() => setView('other')}>Other pools</button>
        </div>
      </div>

      {view === 'review' ? (
        <div className="copypools-review-scroll">
          <CopyReview categories={allCategories} />
        </div>
      ) : (
      <>

      {error && (
        <div className="copypools-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="copypools-body">
        <nav className="copypools-categories" aria-label="Copy pool categories">
          {groups.map((g) => (
            <div key={g.group} className="copypools-group">
              <div className="copypools-group-label">{g.group}</div>
              {g.items.map((c) => (
                <button
                  key={c.category}
                  className={`copypools-cat ${selected === c.category ? 'active' : ''} ${c.wired && c.active === 0 ? 'empty-live' : ''}`}
                  onClick={() => setSelected(c.category)}
                >
                  <span className="copypools-cat-label">{c.label}</span>
                  <span className="copypools-cat-count">{c.active}/{c.total}</span>
                  <span className={`copypools-dot ${c.wired ? 'live' : ''}`} title={c.wired ? 'Live on the site' : 'Not rendered yet'} />
                </button>
              ))}
            </div>
          ))}
          {retiredCount > 0 && (
            <label className="copypools-show-retired">
              <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
              Show retired pools ({retiredCount})
            </label>
          )}
        </nav>

        <section className="copypools-lines">
          {current && (
            <>
              <div className="copypools-lines-head">
                <div>
                  <h2 className="copypools-cat-title">{current.group}: {current.label}</h2>
                  <p className="copypools-cat-meta">
                    <code>{current.category}</code> · {current.slot}
                  </p>
                </div>
                <span className={`copypools-status ${current.wired ? 'live' : ''}`}>
                  {current.wired ? 'Live' : 'Not rendered yet'}
                </span>
              </div>

              {current.wired && !loadingLines && activeCount === 0 && (
                <div className="copypools-warn">No active lines. This slot renders empty on the site.</div>
              )}
              {notice && <div className="copypools-warn">{notice}</div>}

              {loadingLines && <p className="copypools-muted">Loading lines...</p>}
              {!loadingLines && lines.length === 0 && <p className="copypools-muted">No lines yet.</p>}

              <ol className="copypools-list">
                {lines.map((l, i) => {
                  const draft = drafts[l.id];
                  const dirty = draft !== undefined && draft !== l.line;
                  return (
                    <li key={l.id} className={`copypools-line ${l.active === 1 ? '' : 'inactive'}`}>
                      <div className="copypools-order">
                        <button disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="Move up">▲</button>
                        <button disabled={busy || i === lines.length - 1} onClick={() => move(i, 1)} aria-label="Move down">▼</button>
                      </div>
                      <textarea
                        className="copypools-text"
                        value={draft ?? l.line}
                        rows={Math.max(1, Math.ceil((draft ?? l.line).length / 90))}
                        onChange={(e) => setDrafts((d) => ({ ...d, [l.id]: e.target.value }))}
                        onKeyDown={(e) => submitOnCmdEnter(e, () => saveLine(l.id))}
                      />
                      <div className="copypools-actions">
                        {dirty ? (
                          <>
                            <button className="primary" disabled={busy} onClick={() => saveLine(l.id)}>Save</button>
                            <button
                              disabled={busy}
                              onClick={() => setDrafts((d) => {
                                const next = { ...d };
                                delete next[l.id];
                                return next;
                              })}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {l.tag && <span className="copypools-tag" title="Word categories this line fits">{l.tag}</span>}
                            <label className="copypools-toggle">
                              <input type="checkbox" checked={l.active === 1} disabled={busy} onChange={() => toggleActive(l)} />
                              Active
                            </label>
                            <button className="danger" disabled={busy} onClick={() => removeLine(l)}>Delete</button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>

              <div className="copypools-add">
                <textarea
                  className="copypools-text"
                  placeholder="Add a line. ASCII only, no em dashes or semicolons. Cmd+Enter to add."
                  value={newLine}
                  rows={2}
                  onChange={(e) => setNewLine(e.target.value)}
                  onKeyDown={(e) => submitOnCmdEnter(e, addLine)}
                />
                <button className="primary" disabled={busy || !newLine.trim()} onClick={addLine}>Add line</button>
              </div>
            </>
          )}
        </section>
      </div>
      </>
      )}
    </div>
  );
}
